import * as React from "react";
import { useNavigate } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { Gift } from "lucide-react";

import { cn } from "@/lib/utils";
import { useStore } from "@/context/StoreContext";
import { SmartImage } from "@/components/common/SmartImage";
import { Badge } from "@/components/ui/badge";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Extract YouTube video ID from URL
const extractYouTubeId = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?\s]+)/);
  return match ? match[1] : null;
};

// Generate URL slug from title
const generateSlug = (title) => {
  if (!title) return "";
  return title
    .toLowerCase()
    .replace(/[türkçeşüöğıİĞÜŞÖÇ]/g, (c) => {
      const map = { 'ı': 'i', 'ğ': 'g', 'ü': 'u', 'ş': 's', 'ö': 'o', 'ç': 'c', 'İ': 'i', 'Ğ': 'g', 'Ü': 'u', 'Ş': 's', 'Ö': 'o', 'Ç': 'c' };
      return map[c] || c;
    })
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim() + '-indir';
};

export const StoreHeroCarousel = () => {
  const { slider, giveaway, isLoaded } = useStore();
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = React.useState(0);
  
  // Track drag state manually
  const isDragging = React.useRef(false);
  const startX = React.useRef(0);

  // Autoplay plugin - mouse hover'ı yoksay
  const autoplayPlugin = React.useRef(
    Autoplay({ 
      delay: 4000, 
      stopOnInteraction: false, 
      stopOnMouseEnter: false,
      stopOnFocusIn: false,
    })
  );

  // Embla Carousel
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { 
      loop: true,
      dragFree: false,
      containScroll: "trimSnaps",
      skipSnaps: false,
    },
    [autoplayPlugin.current]
  );

  // Wheel eventini yakala - slider'ı kontrol et, sayfa scroll etmesin
  React.useEffect(() => {
    if (!emblaApi) return;
    
    const container = emblaApi.rootNode();
    if (!container) return;

    const handleWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      if (e.deltaY > 0) {
        emblaApi.scrollNext();
      } else {
        emblaApi.scrollPrev();
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    
    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [emblaApi]);

  // Track drag using Embla events
  React.useEffect(() => {
    if (!emblaApi) return;
    
    const onPointerDown = (e) => {
      isDragging.current = false;
      startX.current = emblaApi.scrollProgress();
    };
    
    const onScroll = () => {
      // If scroll progress changed significantly, it's a drag
      const currentProgress = emblaApi.scrollProgress();
      if (Math.abs(currentProgress - startX.current) > 0.01) {
        isDragging.current = true;
      }
    };
    
    const onSettle = () => {
      // Reset after settle with small delay
      setTimeout(() => {
        isDragging.current = false;
      }, 100);
    };
    
    emblaApi.on("pointerDown", onPointerDown);
    emblaApi.on("scroll", onScroll);
    emblaApi.on("settle", onSettle);
    
    return () => {
      emblaApi.off("pointerDown", onPointerDown);
      emblaApi.off("scroll", onScroll);
      emblaApi.off("settle", onSettle);
    };
  }, [emblaApi]);

  // Track slide index
  React.useEffect(() => {
    if (!emblaApi) return;
    
    const onSelect = () => setCurrentIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  // Build slides: giveaway first (if active), then normal slides
  const allSlides = React.useMemo(() => {
    const sorted = [...slider].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    if (giveaway) {
      const giveawaySlide = {
        id: "giveaway",
        title: giveaway.title,
        subtitle: giveaway.prize ? `Odul: ${giveaway.prize}` : giveaway.description,
        image: giveaway.sliderImage || "",
        isGiveaway: true,
        videoUrl: giveaway.videoUrl,
        videoType: giveaway.videoType,
      };
      return [giveawaySlide, ...sorted];
    }
    return sorted;
  }, [slider, giveaway]);

  const count = allSlides.length;

  // Update current index on slide change
  React.useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setCurrentIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  const goToSlide = (index) => {
    if (emblaApi) emblaApi.scrollTo(index);
  };

  // Handle slide click - only navigate if not dragging
  const handleSlideClick = (slide) => {
    // If was dragging, don't navigate
    if (isDragging.current) {
      return;
    }
    
    if (slide.isGiveaway) {
      navigate("/cekilis");
    } else {
      navigate(`/${generateSlug(slide.title)}`);
    }
  };

  if (count === 0) return null;

  // Don't render until all data (including giveaway check) is loaded from API
  if (!isLoaded) return null;

  return (
    <div className="relative">
      <div 
        ref={emblaRef}
        className="overflow-hidden rounded-2xl sm:rounded-[28px] border border-border/60 bg-card shadow-elevated cursor-grab active:cursor-grabbing"
      >
        <div className="flex touch-pan-y">
          {allSlides.map((slide, index) => (
            <div
              key={slide.id}
              className="flex-[0_0_100%] min-w-0"
            >
              <div 
                className="relative h-[200px] sm:h-[280px] md:h-[320px] select-none cursor-pointer"
                onClick={() => handleSlideClick(slide)}
              >
                {/* Giveaway slide with video */}
                {slide.isGiveaway && slide.videoType === "youtube" && extractYouTubeId(slide.videoUrl) ? (
                  <iframe
                    src={`https://www.youtube.com/embed/${extractYouTubeId(slide.videoUrl)}?autoplay=1&mute=1&loop=1&playlist=${extractYouTubeId(slide.videoUrl)}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1`}
                    className="absolute inset-0 w-full h-full pointer-events-none"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
                    title={slide.title}
                  />
                ) : slide.isGiveaway && slide.videoType === "uploaded" && slide.videoUrl ? (
                  <video
                    src={slide.videoUrl.startsWith("http") ? slide.videoUrl : `${API_URL}${slide.videoUrl}`}
                    className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                    autoPlay muted loop playsInline
                  />
                ) : (
                  <SmartImage
                    alt={slide.title}
                    src={slide.image}
                    className="absolute inset-0 pointer-events-none"
                    imgClassName="object-cover"
                    loading={index === 0 ? "eager" : "lazy"}
                  />
                )}

                <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/35 to-background/30 pointer-events-none" />

                {/* Giveaway badge */}
                {slide.isGiveaway && (
                  <div className="absolute top-4 left-4 z-10 pointer-events-none">
                    <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white border-0 gap-1.5 text-sm px-3 py-1">
                      <Gift className="size-4" />
                      Cekilis
                    </Badge>
                  </div>
                )}

                <div className="relative flex h-full flex-col justify-end p-4 sm:p-5 md:p-7 pointer-events-none">
                  <div className="mt-3 sm:mt-4 max-w-[60ch]">
                    <div className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl tracking-tight">
                      {slide.title}
                    </div>
                    <div className="mt-1.5 sm:mt-2 text-xs sm:text-sm md:text-base text-muted-foreground line-clamp-2">
                      {slide.subtitle}
                    </div>
                    {slide.isGiveaway && (
                      <div className="mt-2 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/20 text-primary text-xs sm:text-sm font-medium pointer-events-auto">
                        <Gift className="size-3.5" />
                        Katilmak icin tikla
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Dot indicators */}
      {count > 1 && (
        <div className="flex justify-center gap-1.5 sm:gap-2 mt-3 sm:mt-4">
          {allSlides.map((slide, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={cn(
                "rounded-full transition-all duration-300",
                currentIndex === index
                  ? slide.isGiveaway
                    ? "w-6 sm:w-8 h-2 sm:h-3 bg-yellow-500 shadow-[0_0_10px_rgba(234,179,8,0.5)]"
                    : "w-6 sm:w-8 h-2 sm:h-3 bg-primary shadow-[0_0_10px_rgba(168,85,247,0.5)]"
                  : "w-2 sm:w-3 h-2 sm:h-3 bg-muted-foreground/30 hover:bg-muted-foreground/50"
              )}
              aria-label={slide.isGiveaway ? "Cekilis" : `Slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
